import * as React from 'react';
import { memo, useCallback } from 'react';

import { Popover } from 'antd';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useNotificationBadge,
  useNotificationCenter,
  useNotificationFeed,
  useNotificationPrefs,
  useNotificationSound,
} from 'core/hooks';

import * as S from './NotificationCenter.styled';
import { NotificationPanel } from './NotificationPanel';

/**
 * Колокольчик в шапке ERP (ТЗ 0068).
 *
 * Панель — поповер antd, и это решение, а не привычка. Абсолютный `div`
 * внутри шапки обрезался бы: `MainTemplate` ставит `overflow: hidden` на
 * `Paper` в режиме `fluid`, а `fluid` — это `/dashboard` и `/communicator`,
 * два самых посещаемых экрана. Поповер уходит в `document.body` и этой
 * ловушки не знает; заодно мы не пишем ни одного `z-index` — число ставит
 * antd, и сторож `layers.guard` остаётся нетронутым.
 */
export const NotificationCenter = memo(() => {
  const { t } = useTranslation();
  const center = useNotificationCenter();
  const badge = useNotificationBadge();
  const refreshBadge = useCallback(() => {
    badge.refetch();
  }, [badge]);
  const feed = useNotificationFeed(center.filter, !center.open, refreshBadge);
  // 🔴 БЕЗ `skip`. Настройки нужны звуку постоянно, а он звучит именно при
  // ЗАКРЫТОЙ панели. С `skip: !open` Apollo отдавал `data: undefined` (не
  // «последнее из кеша»), поэтому `soundEnabled` всегда был false, слушатель
  // первого клика не вешался, аудио не разблокировалось — и не существовало
  // состояния, в котором сигнал прозвучал бы. Запрос лёгкий: одна строка
  // настроек, кешируется Apollo.
  const prefsApi = useNotificationPrefs();

  const { unlockAndPreview } = useNotificationSound({
    alarm: badge.soundAlarm,
    soundEnabled: Boolean(prefsApi.prefs?.soundEnabled),
    repeatMinutes: prefsApi.prefs?.repeatAfterMinutes ?? 30,
    quietFrom: prefsApi.prefs?.quietFrom,
    quietTo: prefsApi.prefs?.quietTo,
    panelOpen: center.open,
    mutedUntil: center.mutedUntil,
    unlocked: center.audioUnlocked,
    onUnlocked: center.markAudioUnlocked,
    refetchBadge: badge.refetch,
  });

  const savePrefs = useCallback(
    async (input: Parameters<typeof prefsApi.save>[0]) => {
      const ok = await prefsApi.save(input);
      // Настройки влияют на темп опроса и на повод для звука — освежаем
      // счётчик, чтобы новые значения начали действовать сразу.
      await badge.refetch();
      return ok;
    },
    [badge, prefsApi],
  );

  const soundLocked =
    Boolean(prefsApi.prefs?.soundEnabled) && !center.audioUnlocked;

  return (
    <>
      <S.PopoverStyles />
      <Popover
        trigger="click"
        placement="bottomRight"
        overlayClassName="notification-center-popover"
        visible={center.open}
        onVisibleChange={(visible) =>
          visible ? center.openPanel() : center.closePanel()
        }
        content={
          <div
            // antd v4 не закрывает поповер по Escape сам.
            onKeyDown={(event) => {
              if (event.key === 'Escape') center.closePanel();
            }}
            role="presentation"
          >
            <NotificationPanel
              feed={feed}
              pane={center.pane}
              filter={center.filter}
              prefs={prefsApi.prefs}
              prefsError={prefsApi.error}
              saving={prefsApi.saving}
              audioUnlocked={center.audioUnlocked}
              soundLocked={soundLocked}
              alarmActive={badge.soundAlarm.active}
              onSetPane={center.setPane}
              onSetFilter={center.setFilter}
              onClose={center.closePanel}
              onSnooze={center.snoozeForAnHour}
              onSavePrefs={savePrefs}
              onPreviewSound={unlockAndPreview}
              onStartTelegramLink={prefsApi.startTelegramLink}
              onDropTelegramLink={prefsApi.dropTelegramLink}
              onRefetchPrefs={prefsApi.refetch}
            />
          </div>
        }
      >
        <S.BellButton
          type="button"
          $active={center.open}
          aria-label={t('notifications.bell.aria')}
          title={t('notifications.bell.tooltip')}
        >
          <Bell size={20} />
          {badge.unreadCount > 0 && (
            <S.Badge>
              {badge.unreadCount > 99 ? '99+' : badge.unreadCount}
            </S.Badge>
          )}
          {badge.unreadCount === 0 && soundLocked && <S.LockedDot />}
        </S.BellButton>
      </Popover>
    </>
  );
});

NotificationCenter.displayName = 'NotificationCenter';
